/** task-info の 1 件（図のハイライトに必要な最小情報）。 */
export type DiagramTaskState = {
  /** BPMN 要素の id。図の要素と突き合わせる。 */
  readonly bpmnIdentifier: string | null;
  readonly state: string | null;
};

/** 図に付けるハイライト。要素 id の配列。 */
export type DiagramMarks = {
  /** いま止まっている場所（承認待ちなど）。 */
  readonly current: readonly string[];
  /** 通過済み。 */
  readonly completed: readonly string[];
  /** 失敗した場所。 */
  readonly error: readonly string[];
};

/** 進行中として扱う状態。待ちタスクも「今ここ」に含める。 */
const CURRENT_STATES = new Set(["READY", "WAITING", "STARTED"]);
const COMPLETED_STATES = new Set(["COMPLETED"]);
const ERROR_STATES = new Set(["ERROR"]);

export type DeriveDiagramMarksOptions = {
  /**
   * 案件全体がエラーで止まっているか。
   *
   * SpiffArena は**失敗したタスクを ERROR 状態にしない**（実測: status=error の案件でも
   * task-info は READY のまま。再実行できるよう残していると思われる）。履歴の
   * `process_instance_error` にも要素 id が無いため、task の state だけを見ると図が
   * 何も示さない。そこで案件がエラーなら「止まっている場所」を失敗箇所として扱う。
   */
  readonly isErrored?: boolean;
};

/**
 * タスクの状態から図のハイライトを決める。
 *
 * FUTURE / CANCELLED は色を付けない（通っていない道を強調すると誤読される）。
 * ループで同じ要素が完了と現在地の両方に来ることがあるため、現在地を優先する。
 */
export function deriveDiagramMarks(
  tasks: readonly DiagramTaskState[],
  options: DeriveDiagramMarksOptions = {},
): DiagramMarks {
  const current = new Set<string>();
  const completed = new Set<string>();
  const error = new Set<string>();

  for (const task of tasks) {
    const id = task.bpmnIdentifier;
    if (id === null || id === "") continue;

    const state = task.state ?? "";
    if (ERROR_STATES.has(state)) error.add(id);
    else if (CURRENT_STATES.has(state)) current.add(id);
    else if (COMPLETED_STATES.has(state)) completed.add(id);
  }

  // 案件がエラーなら、止まっている場所が失敗箇所（現在地としては出さない。
  // 同じ要素が 2 色になると読めない）
  if (options.isErrored === true) {
    for (const id of current) error.add(id);
    current.clear();
  }

  // 現在地・失敗箇所になっている要素は完了扱いにしない
  for (const id of current) completed.delete(id);
  for (const id of error) completed.delete(id);

  return {
    current: [...current],
    completed: [...completed],
    error: [...error],
  };
}
